import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ClassDetails } from '../../types/courseTypes';
import { COLORS } from "../../theme/Colors";
import { UI_TEXT } from "../../constants/messages";

interface StudentClassItemProps {
  item: ClassDetails;
  onClassClick?: (item: ClassDetails) => void;
}

const StudentClassItem: React.FC<StudentClassItemProps> = ({ item, onClassClick }) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastClickTimeRef = useRef<number>(0);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);
  const dragThreshold = 5; // pixels - more sensitive

  const thumbnailUrl = "https://plus.unsplash.com/premium_photo-1682787494977-d013bb5a8773?q=80&w=3270";
  const rating = 5;
  const classNumberText = `CLASS ${item.classId}`;

  // Handle mouse/touch start
  const handlePointerStart = useCallback((e: React.PointerEvent) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    
    startPositionRef.current = { x: clientX, y: clientY };
    setIsPressed(true);
    setIsDragging(false);
  }, []);

  // Handle mouse/touch move
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!startPositionRef.current) return;
    
    const clientX = e.clientX;
    const clientY = e.clientY;
    const deltaX = Math.abs(clientX - startPositionRef.current.x);
    const deltaY = Math.abs(clientY - startPositionRef.current.y);
    
    // If movement exceeds threshold, consider it a drag
    if (deltaX > dragThreshold || deltaY > dragThreshold) {
      setIsDragging(true);
      setIsPressed(false);
    }
  }, [dragThreshold]);

  // Handle mouse/touch end
  const handlePointerEnd = useCallback((e: React.PointerEvent) => {
    const wasDragging = isDragging;
    const wasPressed = isPressed;
    
    setIsPressed(false);
    setIsDragging(false);
    startPositionRef.current = null;
    
    // Only trigger click if it wasn't a drag AND it was a proper click (not a leave event)
    if (!wasDragging && wasPressed && e.type === 'pointerup') {
      // Add a small delay to ensure drag detection is complete
      setTimeout(() => {
        const now = Date.now();
        const timeSinceLastClick = now - lastClickTimeRef.current;
        
        // Prevent rapid clicks (debounce with 300ms)
        if (timeSinceLastClick < 300) {
          return;
        }
        
        lastClickTimeRef.current = now;
        
        // Clear any existing timeout
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
        }
        
        // Add slight delay for better UX
        clickTimeoutRef.current = setTimeout(() => {
          if (onClassClick) {
            onClassClick(item);
          }
        }, 100);
      }, 50);
    }
  }, [isDragging, isPressed, item.fromDate, item.endDate]);

  // Handle pointer leave (mouse out) - just reset state without triggering click
  const handlePointerLeave = useCallback(() => {
    setIsPressed(false);
    setIsDragging(false);
    startPositionRef.current = null;
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  return (
    <CardContainer 
      onPointerDown={handlePointerStart}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerLeave={handlePointerLeave}
      $isPressed={isPressed}
      $isDragging={isDragging}
    >
      <ThumbnailContainer>
        <Thumbnail src={thumbnailUrl} alt={item.name || "Class thumbnail"} />
      </ThumbnailContainer>

      <ContentContainer>
        <ClassInfo>
          <ClassIdentifier>{classNumberText}</ClassIdentifier>
          <ClassName>{item.name || "Unnamed Class"}</ClassName>
        </ClassInfo>

        <TutorAndSubject>
          <TutorInfo>{item.tutorName || "N/A"}</TutorInfo>
          {item.subject && <SubjectTag>{item.subject}</SubjectTag>}
        </TutorAndSubject>

        {item.location && (
          <LocationTypeTag $isOnline={item.location === 'online'}>
            {item.location === 'online' ? '🌐 Online' : '📍 In-Person'}
          </LocationTypeTag>
        )}

        <Price>${item.fees != null ? item.fees.toFixed(2) : '0.00'}</Price>
      </ContentContainer>
    </CardContainer>
  );
};

const CardContainer = styled.div<{ $isPressed: boolean; $isDragging: boolean }>`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-height: 380px;
  height: 380px;
  touch-action: pan-x pan-y;
  user-select: none;
  position: relative;
  box-sizing: border-box;

  /* Fluid animations based on state */
  transform: ${({ $isPressed, $isDragging }) => {
    if ($isDragging) return 'scale(1)';
    if ($isPressed) return 'scale(0.98) translateY(2px)';
    return 'scale(1) translateY(0)';
  }};

  box-shadow: ${({ $isPressed, $isDragging }) => {
    if ($isDragging) return '0px 4px 12px rgba(0, 0, 0, 0.1)';
    if ($isPressed) return '0px 2px 8px rgba(0, 0, 0, 0.15)';
    return '0px 4px 12px rgba(0, 0, 0, 0.1)';
  }};

  &:hover:not([data-dragging="true"]) {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Ensure consistent sizing in slider */
  @media (max-width: 768px) {
    min-height: 360px;
    height: 360px;
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  background-color: #f0f0f0;
  overflow: hidden;
  flex-shrink: 0;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  ${CardContainer}:hover & {
    transform: scale(1.05);
  }
`;

const RatingBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  backdrop-filter: blur(2px);
`;

const StarIcon = styled.span`
  color: #ffd700;
  margin-right: 4px;
  font-size: 16px;
`;

const ContentContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
  min-height: 0;
`;

const ClassInfo = styled.div`
  margin-bottom: 8px;
`;

const ClassIdentifier = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0 0 2px 0;
  text-transform: uppercase;
`;

const ClassName = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 2.6em;
`;

const TutorAndSubject = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
`;

const TutorInfo = styled.p`
  color: #555;
  margin: 0;
  display: flex;
  align-items: baseline;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  margin-right: 8px;
`;

const TutorMetaText = styled.span`
  font-size: 12px;
  color: #777;
`;

const SubjectTag = styled.span`
  background-color: ${COLORS.backgroundLight || '#e0e7ff'};
  color: ${COLORS.black || '#4338ca'};
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 12px;
`;

const LocationTypeTag = styled.div<{ $isOnline: boolean }>`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.$isOnline ? '#e0f2fe' : '#fef3c7'};
  color: ${props => props.$isOnline ? '#0369a1' : '#d97706'};
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 12px;
  margin-bottom: 8px;
`;

const Price = styled.p`
  font-size: 22px;
  font-weight: bold;
  color: ${COLORS.primary};
  margin: 0;
  margin-top: auto;
`;

const MemoizedStudentClassItem = React.memo(StudentClassItem);
MemoizedStudentClassItem.displayName = 'StudentClassItem';

export default MemoizedStudentClassItem;
